import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { openToast } from "@utils/toastStore";
import EnrollmentListShell from "../components/EnrollmentListShell";
import WaitingUserRow from "../components/WaitingUserRow";
import { useWaitingUsersList } from "../hooks/useWaitingUsersList";
import { useApproveOrDeclineEnrollment } from "../hooks/useApproveOrDeclineEnrollment";

const PAGE_SIZE = 10;

interface WaitingUsersTabProps {
  corporateId: string | number;
  objectId: number;
  editionId?: number;
}

const WaitingUsersTab: React.FC<WaitingUsersTabProps> = ({
  corporateId,
  objectId,
  editionId,
}) => {
  const { t } = useTranslation("users-and-groups");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [pendingId, setPendingId] = useState<string | number | null>(null);

  const { items, totalItems, totalPages, isLoading, mutate } =
    useWaitingUsersList({
      corporateId,
      objectId,
      editionId,
      page,
      pageSize: PAGE_SIZE,
      search: search || undefined,
    });

  const { submit, isMutating } = useApproveOrDeclineEnrollment();

  const handleApproveToggle = async (
    userId: string | number,
    approve: boolean,
  ) => {
    if (!userId) return;
    setPendingId(userId);
    try {
      await submit({
        studentEid: userId,
        corporateId: Number(corporateId),
        objectId,
        editionId,
        approve,
      });
      openToast({
        severity: "success",
        message: String(
          t(approve ? "approve.success-approved" : "approve.success-declined"),
        ),
      });
      mutate();
    } catch (e: any) {
      openToast({
        severity: "error",
        message: e?.message || String(t("approve.error")),
      });
    } finally {
      setPendingId(null);
    }
  };

  return (
    <EnrollmentListShell
      search={search}
      onSearchSubmit={(v) => {
        setSearch(v);
        setPage(1);
      }}
      hideSearch
      isLoading={isLoading}
      isEmpty={items.length === 0}
      emptyLabel={String(t("empty.waiting-users"))}
      page={page}
      pageSize={PAGE_SIZE}
      totalItems={totalItems}
      totalPages={totalPages}
      onPageChange={setPage}
    >
      {items.map((user) => (
        <WaitingUserRow
          key={`${user.userId}-${user.objectId ?? ""}`}
          user={user}
          approved={false}
          disabled={isMutating && pendingId === user.userId}
          onApproveToggle={(next) =>
            user.userId && handleApproveToggle(user.userId, next)
          }
          // TODO: riattivare la freccia quando esisterà il detail enrollment
          // onArrowClick={(u) => navigate(...)}
        />
      ))}
    </EnrollmentListShell>
  );
};

export default WaitingUsersTab;
